/* aw-order-widget.js
 * Vanilla order widget + bundle card handlers for the water delivery page.
 * Replaces the compiled React widget. A few KB, no dependencies, native controls
 * only (no keyboard pop on mobile). Posts standard Shopify product form fields
 * (variant id, quantity, selling_plan) so Appstle subscriptions work unchanged,
 * and adds the matched refundable deposit quantity in the same /cart/add.js call.
 */
const dataEl = document.getElementById('aw-widget-data');
const rootEl = document.getElementById('aw-order-widget');
if (dataEl && rootEl) init(JSON.parse(dataEl.textContent));

/* One customer-facing money format ("$104" / "$23.50" / "$1,056"): flush dollar
 * sign, two decimals only when there are cents, no USD suffix. Mirrors
 * aw-money.liquid and usd() in aw-cart-sync.js; keep the three in sync. */
function usd(cents) {
  const n = cents / 100;
  const digits = n % 1 === 0 ? 0 : 2;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

/* Composition -> first-order discount code, mirroring the codes in Shopify
 * admin (starter22 / home77 / family33, config verified 2026-07-16). Each code
 * requires a minimum count of ELIGIBLE items - jugs + dispenser; deposits and
 * the shelf never count toward a minimum: starter22 needs 3, home77 needs 5
 * plus the dispenser, family33 needs 7. Mirrored in aw-cart-sync.js; keep the
 * two in sync. */
function offerCode(waterQty, hasDispenser) {
  const eligible = waterQty + (hasDispenser ? 1 : 0);
  if (waterQty < 1) return null;
  if (waterQty <= 3) return eligible >= 3 ? 'starter22' : null;
  if (waterQty <= 5) return hasDispenser && eligible >= 5 ? 'home77' : null;
  return eligible >= 7 ? 'family33' : null;
}

/* ZIP prefix -> region variant title, matching the served-ZIP footprint
 * (005 + 070-089 + 100-149 -> NY/NJ, 970-979 + 986 -> OR, else CA/FL/TX).
 * Shared logic with variant C's guided flow. String input keeps leading-zero
 * ZIPs safe. Used to auto-select the region dropdown from the customer's ZIP
 * (Brian, 2026-07-19) so an Oregon ZIP prices at OR $22 without a manual pick. */
function zipRegionKey(zip) {
  const p = Number(String(zip).slice(0, 3));
  if (p === 5 || (p >= 70 && p <= 89) || (p >= 100 && p <= 149)) return 'NY/NJ';
  if ((p >= 970 && p <= 979) || p === 986) return 'OR';
  return 'CA/FL/TX';
}

/* Best-effort clear of Shopify's discount cookie when the composition matches
 * no offer. The codes' own minimum-quantity rules are the real guard at
 * checkout; this just avoids carrying a stale code forward. */
function clearDiscountCookie() {
  const expire = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = expire;
  document.cookie = expire + '; domain=.' + location.hostname.replace(/^www\./, '');
}

function init(D) {
  const state = {
    variantId: null,
    variantCents: 0,
    qty: 4,
    weeks: 4,
    planId: null,
    eligibility: readEligibility(),
  };

  const $ = (sel) => rootEl.querySelector(sel);
  const $$ = (sel) => Array.from(rootEl.querySelectorAll(sel));
  const out = (name) => rootEl.querySelector('[data-out="' + name + '"]');

  const regionSel = $('#aw-region');
  const freqMore = $('#aw-freq-more');
  const dispenser = $('#aw-addon-dispenser');
  const shelf = $('#aw-addon-shelf');
  const addBtn = $('#aw-add-to-cart');
  // ZIP is required before ordering, but only when the gate is on the page.
  const gateEl = document.getElementById('aw-zip-gate');

  function readEligibility() {
    try { return JSON.parse(localStorage.getItem('aw_delivery')) || null; } catch (_) { return null; }
  }

  function planForWeeks(weeks) {
    const re = new RegExp('every\\s+' + weeks + '\\s+week', 'i');
    const one = weeks === 1 ? D.plans.find((p) => /every\s+week/i.test(p.name)) : null;
    return D.plans.find((p) => re.test(p.name)) || one || null;
  }

  function routeWeeks() {
    const el = state.eligibility || readEligibility();
    const w = el && el.state === 'served' && el.cadence && Number(el.cadence.weeks);
    return w > 0 ? w : null;
  }

  function weeksFromPlanName(name) {
    const m = String(name || '').match(/every\s+(\d+)?\s*week/i);
    return m ? (m[1] ? Number(m[1]) : 1) : null;
  }

  // Region-cadence enforcement (Brian, 2026-07-18): in cadence regions the
  // frequency choices are limited to multiples of the route cadence, so a
  // subscription can never bill faster than its route runs.
  function applyCadenceUI() {
    const rw = routeWeeks();
    $$('.aw-freq__btn').forEach((b) => {
      b.hidden = !!rw && Number(b.dataset.weeks) % rw !== 0;
    });
    Array.from(freqMore.options).forEach((o) => {
      if (!o.value) return;
      const w = weeksFromPlanName(o.dataset.name);
      const bad = !!rw && (!w || w % rw !== 0);
      o.hidden = bad;
      o.disabled = bad;
    });
    if (rw && state.weeks % rw !== 0) {
      setWeeks(rw);
      freqMore.value = state.planId ? String(state.planId) : '';
      render();
    }
  }

  function syncRegion() {
    const opt = regionSel.selectedOptions[0];
    state.variantId = Number(opt.value);
    state.variantCents = Number(opt.dataset.price);
  }

  // Auto-select the region dropdown from a resolved ZIP so pricing/variant
  // match the delivery address (Brian, 2026-07-19). Only moves to an AVAILABLE
  // option; leaves the customer's manual choice alone if none matches.
  function regionFromZip(zip) {
    if (!zip) return;
    const want = zipRegionKey(zip);
    const opt = Array.from(regionSel.options).find((o) => o.dataset.region === want && !o.disabled);
    if (opt && opt.value !== regionSel.value) {
      regionSel.value = opt.value;
      syncRegion();
    }
  }

  function perBottleCents() {
    const onCurve = String(state.variantId) === String(D.curveVariantId);
    if (!onCurve) return state.variantCents;
    if (D.curve[String(state.qty)]) return D.curve[String(state.qty)];
    if (state.qty > 12) return D.curve['12'];
    return state.variantCents; // trial sizes 1 to 3 bill the base bottle price
  }

  function addonsCents() {
    let sum = 0;
    if (dispenser.checked) sum += Number(dispenser.dataset.price);
    if (shelf.checked) sum += Number(shelf.dataset.price);
    return sum;
  }

  function render() {
    const per = perBottleCents();
    const water = per * state.qty;
    const deposits = D.depositCents * state.qty;
    const addons = addonsCents();
    const fee = state.eligibility && state.eligibility.state === 'surcharge' ? state.eligibility.fee * 100 : 0;

    // First-order offers, mirroring the discount codes in Shopify admin
    // (applyOffer() auto-applies the matching code at add to cart). Deposits
    // and the delivery fee never ride a discount; the shelf only rides the
    // family 33, so the starter math excludes it.
    let discount = 0;
    let offerLabel = '';
    const code = D.offersLive ? offerCode(state.qty, dispenser.checked) : null;
    if (code === 'starter22') {
      const dispCents = dispenser.checked ? Number(dispenser.dataset.price) : 0;
      discount = Math.round(0.22 * (water + dispCents));
      offerLabel = 'Starter offer: 22% off, deposits excluded';
    } else if (code === 'home77') {
      // home77 is a flat $77 off in admin, sized to the dispenser's price;
      // if the dispenser price ever changes, update the code's amount too.
      discount = Number(dispenser.dataset.price);
      offerLabel = 'Home offer: free dispenser';
    } else if (code === 'family33') {
      discount = Math.round(0.33 * (water + addons));
      offerLabel = 'Family offer: 33% off, deposits excluded';
    }
    const total = water + deposits + addons + fee - discount;

    out('water-label').textContent = state.qty + ' glass jug' + (state.qty === 1 ? '' : 's') + ' of spring water (' + usd(per) + ' per jug)';
    out('water-price').textContent = usd(water);
    out('deposit-price').textContent = usd(deposits);

    const addonRow = rootEl.querySelector('[data-row="addons"]');
    addonRow.hidden = addons === 0;
    if (addons > 0) {
      const names = [];
      if (dispenser.checked) names.push('Dispenser');
      if (shelf.checked) names.push('White Steel Shelf');
      out('addons-label').textContent = names.join(' + ');
      out('addons-price').textContent = usd(addons);
    }

    const feeRow = rootEl.querySelector('[data-row="fee"]');
    feeRow.hidden = fee === 0;
    if (fee > 0) out('fee-price').textContent = usd(fee) + ' per delivery';

    const offerRow = rootEl.querySelector('[data-row="offer"]');
    offerRow.hidden = discount === 0;
    if (discount > 0) {
      out('offer-label').textContent = offerLabel + ' (new customer offer, applied automatically at checkout)';
      out('offer-amount').textContent = '-' + usd(discount);
    }

    out('total-price').textContent = usd(total);

    const cadence = state.weeks === 1 ? 'every week' : 'every ' + state.weeks + ' weeks';
    const trialNote = out('trial-note');
    if (state.qty < 4) {
      const onCurve = String(state.variantId) === String(D.curveVariantId);
      const renew = (onCurve && D.curve['4'] ? D.curve['4'] : state.variantCents) * 4;
      const topup = D.depositCents * (4 - state.qty);
      out('renew-price').textContent = usd(renew);
      out('renew-cadence').textContent = cadence + ' on the 4 jug plan';
      trialNote.hidden = false;
      trialNote.textContent = 'Trial size: your first delivery is ' + state.qty + ' glass jug' + (state.qty === 1 ? '' : 's') + '. Deliveries after that continue on our 4 jug minimum at ' + usd(renew) + ' per delivery, plus a one time ' + usd(topup) + ' refundable deposit top up on your first full delivery.';
    } else {
      out('renew-price').textContent = usd(water);
      out('renew-cadence').textContent = cadence + ', the published price, no surprises';
      trialNote.hidden = true;
    }

    const waitlisted = state.eligibility && state.eligibility.state === 'waitlist';
    const needsZip = !state.eligibility && !!gateEl;
    addBtn.disabled = !!waitlisted;
    addBtn.classList.toggle('aw-btn--needs-zip', needsZip);
    addBtn.textContent = waitlisted ? 'Join the waitlist above'
      : needsZip ? 'Enter your ZIP to order'
      : 'Add to cart';
  }

  function setQty(q) {
    state.qty = Number(q);
    $$('.aw-qty__btn').forEach((b) => b.classList.toggle('is-active', Number(b.dataset.qty) === state.qty));
    render();
  }

  function setWeeks(weeks, planIdOverride) {
    const rw = routeWeeks();
    if (rw && !planIdOverride && Number(weeks) % rw !== 0) weeks = rw;
    state.weeks = Number(weeks);
    const plan = planIdOverride ? { id: planIdOverride } : planForWeeks(state.weeks);
    state.planId = plan ? plan.id : null;
    $$('.aw-freq__btn').forEach((b) => b.classList.toggle('is-active', Number(b.dataset.weeks) === state.weeks && !planIdOverride));
    render();
  }

  // Controls
  regionSel.addEventListener('change', () => { syncRegion(); render(); });
  $$('.aw-qty__btn').forEach((b) => b.addEventListener('click', () => setQty(b.dataset.qty)));
  $$('.aw-freq__btn').forEach((b) => b.addEventListener('click', () => { freqMore.value = ''; setWeeks(b.dataset.weeks); }));
  freqMore.addEventListener('change', () => {
    if (!freqMore.value) return;
    const name = freqMore.selectedOptions[0].dataset.name || '';
    const m = name.match(/every\s+(\d+)?\s*week/i);
    setWeeks(m && m[1] ? m[1] : 1, Number(freqMore.value));
  });
  dispenser.addEventListener('change', render);
  shelf.addEventListener('change', render);

  const tipBtn = rootEl.querySelector('[data-tip]');
  const tip = document.getElementById('aw-deposit-tip');
  tipBtn.addEventListener('click', () => {
    const open = tip.classList.toggle('is-open');
    tipBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  document.addEventListener('aw:eligibility', (e) => {
    state.eligibility = e.detail;
    if (e.detail && e.detail.zip) regionFromZip(e.detail.zip);
    applyCadenceUI();
    render();
  });

  // Bundle cards. A chosen card shows an active state (Siera, 2026-07-16 call:
  // pricing updated but "it still looks like I'm on Home"); manual edits to the
  // widget clear it, since the order no longer matches a card.
  function markChosen(chosenCard) {
    document.querySelectorAll('.aw-bundle').forEach((c) => {
      const on = c === chosenCard;
      c.classList.toggle('is-chosen', on);
      const b = c.querySelector('[data-bundle-choose]');
      if (b) {
        b.textContent = on ? 'Your plan' : b.dataset.label;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    });
  }
  const clearChosen = () => markChosen(null);
  const controls = rootEl.querySelector('.aw-widget__controls');
  controls.addEventListener('change', clearChosen, true);
  controls.addEventListener('click', (e) => {
    if (e.target.closest('.aw-qty__btn, .aw-freq__btn')) clearChosen();
  }, true);

  document.querySelectorAll('.aw-bundle').forEach((card) => {
    const chooseBtn = card.querySelector('[data-bundle-choose]');
    const sizeBtns = card.querySelectorAll('[data-family-size]');
    const priceEl = card.querySelector('[data-family-price]');
    const renewsEl = card.querySelector('[data-family-renews]');
    if (chooseBtn) chooseBtn.dataset.label = chooseBtn.textContent;

    sizeBtns.forEach((b) => b.addEventListener('click', () => {
      sizeBtns.forEach((x) => x.classList.toggle('is-active', x === b));
      card.dataset.qty = b.dataset.familySize;
      const n = b.dataset.familySize;
      if (priceEl) priceEl.innerHTML = usd(Number(priceEl.dataset['price' + n])) + ' <span>first delivery with ' + n + ' jugs, all in</span>';
      if (renewsEl) renewsEl.textContent = 'Renews at ' + usd(Number(renewsEl.dataset['renew' + n])) + ' per delivery with ' + n + ' jugs (' + usd(Number(renewsEl.dataset['per' + n])) + ' per jug).';
      if (card.classList.contains('is-chosen') && chooseBtn) chooseBtn.click(); // size change re-applies the chosen plan
    }));

    if (chooseBtn) chooseBtn.addEventListener('click', () => {
      setQty(card.dataset.qty);
      setWeeks(4);
      const addons = (card.dataset.addons || '').split(',');
      dispenser.checked = addons.some((a) => a.indexOf('dispenser') === 0);
      shelf.checked = addons.indexOf('shelf') > -1;
      render();
      markChosen(card);
      rootEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  // Add to cart: water + matched deposits + add-ons in one call.
  addBtn.addEventListener('click', async () => {
    // No ZIP checked yet: send them to the gate instead of the cart.
    if (!state.eligibility && gateEl) {
      gateEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const zipInput = document.getElementById('aw-zip-input');
      if (zipInput) zipInput.focus({ preventScroll: true });
      return;
    }
    const err = out('error');
    err.hidden = true;
    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';
    try {
      // "Add to cart" SETS the plan lines (2026-07-19 back-port from variant
      // C): zero any existing water/deposit/add-on lines first, so an
      // adjust-and-re-add replaces the order instead of stacking two orders.
      // Aborts to the retry error if the cart cannot be read OR a zeroing
      // write fails - a silently doubled order is worse than a retry.
      // Other products stay untouched.
      const planVariants = [D.depositVariantId, Number(dispenser.dataset.variant), Number(shelf.dataset.variant)]
        .concat(Array.from(regionSel.options).map((o) => Number(o.value))).map(String);
      let existingCart = null;
      for (let i = 0; i < 3 && !existingCart; i++) {
        existingCart = await fetch('/cart.js').then((r) => (r.ok ? r.json() : null)).catch(() => null);
        if (!existingCart) await new Promise((res) => setTimeout(res, 1200));
      }
      if (!existingCart) throw new Error('cart unreadable');
      for (const line of existingCart.items) {
        if (planVariants.indexOf(String(line.variant_id)) === -1) continue;
        // A swallowed zeroing failure followed by the add DOUBLES the order
        // (observed under Cloudflare 429 bursts on C, 2026-07-19): retry
        // once, then abort to the honest retry error like the guard read.
        let zr = null;
        for (let i = 0; i < 2 && !(zr && zr.ok); i++) {
          if (i) await new Promise((res) => setTimeout(res, 900));
          zr = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          }).catch(() => null);
        }
        if (!zr || !zr.ok) throw new Error('cart zero failed');
      }
      const items = [
        { id: state.variantId, quantity: state.qty, selling_plan: state.planId },
        { id: D.depositVariantId, quantity: state.qty },
      ];
      if (dispenser.checked) items.push({ id: Number(dispenser.dataset.variant), quantity: 1 });
      if (shelf.checked) items.push({ id: Number(shelf.dataset.variant), quantity: 1 });

      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) throw new Error('add failed ' + r.status);

      if (state.eligibility) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: {
            'Delivery ZIP': state.eligibility.zip,
            'Delivery Eligibility': state.eligibility.state,
            'Delivery Fee': state.eligibility.fee == null ? '' : String(state.eligibility.fee),
          } }),
        }).catch(() => {});
      }

      await applyOffer();

      openCart();
    } catch (e) {
      err.hidden = false;
      err.textContent = 'Something went wrong adding to your cart. Please try again.';
    } finally {
      addBtn.disabled = false;
      addBtn.textContent = 'Add to cart';
      render();
    }
  });

  /* Auto-apply the first-order discount so checkout matches the promise on
   * the card. 2026-07-19 back-port from variant C: the cart API's discount
   * field REPLACES the cart's whole code set. The old /discount/{code} route
   * only APPENDED, and Shopify then applied whichever attached code paid the
   * customer most - home77 (-$77) beat family33 (-$74.91) on 6 jug carts
   * (Round 1 finding 6). aw-cart-sync.js re-asserts the right code when
   * quantities change inside the cart, keyed off aw_offer_code. */
  async function applyOffer() {
    if (!D.offersLive) return;
    const code = offerCode(state.qty, dispenser.checked);
    try {
      localStorage.setItem('aw_offer_code', code || 'none');
      const r = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code || '' }),
      });
      if (!r.ok) { // legacy fallback: cookie route
        if (code) await fetch('/discount/' + code);
        else clearDiscountCookie();
      }
    } catch (_) { /* non-blocking; the customer can still enter the code manually */ }
  }

  function openCart() {
    // Ask the theme to rebuild and open its drawer; fall back to the cart page.
    document.dispatchEvent(new CustomEvent('cart:build'));
    const trigger = document.querySelector('.js-drawer-open-cart, [data-drawer-toggle="CartDrawer"], a[href$="/cart"]');
    if (trigger) { trigger.click(); return; }
    window.location.href = '/cart';
  }

  // Boot
  syncRegion();
  if (state.eligibility && state.eligibility.zip) regionFromZip(state.eligibility.zip);
  setWeeks(4);
  setQty(4);
  applyCadenceUI();
}

// Product-photo previews: 56px thumbs on the dispenser/shelf rows open the shared
// native <dialog>. Delegated so it works for card and widget thumbs alike.
(function () {
  var dlg = document.getElementById('aw-wd-photo-dialog');
  if (!dlg || typeof dlg.showModal !== 'function') return;
  var img = dlg.querySelector('[data-aw-photo-img]');
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-aw-photo]');
    if (b) {
      e.preventDefault();
      img.src = b.getAttribute('data-full');
      img.alt = b.getAttribute('data-name') || '';
      dlg.showModal();
      return;
    }
    if (e.target.closest && e.target.closest('[data-aw-photo-close]')) {
      if (dlg.open) dlg.close();
      return;
    }
    if (e.target === dlg && dlg.open) dlg.close();
  });
})();
